import { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../Notifications/NotificationBell';
import Footer from '../Footer/Footer';

const Layout = () => {
  const { user, logout, isAuthenticated, isAdmin, isInstructor, isApprovedInstructor, isShortCourseLearner } = useAuth();
  const navigate = useNavigate();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setLogoutLoading(true);

    try {
      await logout();
      navigate('/login', { state: { fromLogout: true } });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login', { state: { fromLogout: true } });
    } finally {
      setLogoutLoading(false);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navLinkClass = 'text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium';
  const mobileLinkClass = 'block text-gray-700 hover:text-primary-600 hover:bg-gray-50 px-3 py-2 text-base font-medium rounded-md transition-colors';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {isAuthenticated && (
        <nav className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <div className="flex items-center flex-shrink-0 min-w-0">
                <Link
                  to="/dashboard"
                  className="text-base sm:text-xl lg:text-2xl font-bold text-primary-600 whitespace-nowrap truncate"
                  onClick={closeMobileMenu}
                >
                  <span className="sm:hidden">ALIEN GREEN</span>
                  <span className="hidden sm:inline">ALIEN GREEN (EduTechnologies)</span>
                </Link>
              </div>

              <div className="hidden md:flex items-center space-x-1">
                <Link to="/dashboard" className={navLinkClass}>Dashboard</Link>
                <Link to="/education-hub" className={navLinkClass}>Learning Hub</Link>
                {!isInstructor && isShortCourseLearner && (
                  <>
                    <Link to="/short-courses" className={navLinkClass}>Browse Courses</Link>
                    <Link to="/my-short-courses" className={navLinkClass}>My Short Courses</Link>
                  </>
                )}
                {!isInstructor && !isShortCourseLearner && (
                  <>
                    <Link to="/content" className={navLinkClass}>Content</Link>
                    <Link to="/exams" className={navLinkClass}>Exams</Link>
                    <Link to="/my-short-courses" className={navLinkClass}>Short Courses</Link>
                    <Link to="/progress" className={navLinkClass}>Progress</Link>
                    <Link to="/reports" className={navLinkClass}>Reports</Link>
                  </>
                )}
                {(isApprovedInstructor || isAdmin) && (
                  <>
                    <Link to="/my-content" className={navLinkClass}>My Content</Link>
                    <Link to="/my-exams" className={navLinkClass}>My Tests</Link>
                    <Link to="/instructor/short-courses" className={navLinkClass}>Short Courses</Link>
                    <Link to="/instructor/earnings" className={navLinkClass}>Earnings</Link>
                    <Link to="/instructor/analytics" className={navLinkClass}>Analytics</Link>
                    <Link to="/instructor/student-results" className={navLinkClass}>Student Results</Link>
                    <Link to="/reports" className={navLinkClass}>Reports</Link>
                  </>
                )}
                <Link to="/payment" className={navLinkClass}>Buy Coins</Link>
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4">
                {user?.coins_balance !== undefined && (
                  <div className="hidden sm:flex items-center space-x-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-1.5">
                    <span className="text-yellow-800 font-semibold">{user.coins_balance}</span>
                    <span className="text-yellow-600 text-sm">coins</span>
                  </div>
                )}

                <NotificationBell />

                <Link to="/profile" className="hidden sm:block text-sm text-gray-700 hover:text-primary-600">
                  {user?.name}
                </Link>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="hidden md:inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  disabled={logoutLoading}
                >
                  {logoutLoading ? 'Logging out...' : 'Logout'}
                </button>

                <button
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                  className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors"
                  aria-label="Toggle menu"
                  type="button"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={mobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                    />
                  </svg>
                </button>
              </div>
            </div>

            {mobileMenuOpen && (
              <div className="md:hidden border-t border-gray-200 py-2 space-y-1 pb-4">
                <Link to="/dashboard" onClick={closeMobileMenu} className={mobileLinkClass}>Dashboard</Link>
                <Link to="/education-hub" onClick={closeMobileMenu} className={mobileLinkClass}>Learning Hub</Link>

                {!isInstructor && isShortCourseLearner && (
                  <>
                    <Link to="/short-courses" onClick={closeMobileMenu} className={mobileLinkClass}>Browse Courses</Link>
                    <Link to="/my-short-courses" onClick={closeMobileMenu} className={mobileLinkClass}>My Short Courses</Link>
                  </>
                )}

                {!isInstructor && !isShortCourseLearner && (
                  <>
                    <Link to="/content" onClick={closeMobileMenu} className={mobileLinkClass}>Content</Link>
                    <Link to="/exams" onClick={closeMobileMenu} className={mobileLinkClass}>Exams</Link>
                    <Link to="/my-short-courses" onClick={closeMobileMenu} className={mobileLinkClass}>Short Courses</Link>
                    <Link to="/progress" onClick={closeMobileMenu} className={mobileLinkClass}>Progress</Link>
                    <Link to="/reports" onClick={closeMobileMenu} className={mobileLinkClass}>Reports</Link>
                  </>
                )}

                {(isApprovedInstructor || isAdmin) && (
                  <>
                    <Link to="/my-content" onClick={closeMobileMenu} className={mobileLinkClass}>My Content</Link>
                    <Link to="/my-exams" onClick={closeMobileMenu} className={mobileLinkClass}>My Tests</Link>
                    <Link to="/instructor/short-courses" onClick={closeMobileMenu} className={mobileLinkClass}>Short Courses</Link>
                    <Link to="/instructor/earnings" onClick={closeMobileMenu} className={mobileLinkClass}>Earnings</Link>
                    <Link to="/instructor/analytics" onClick={closeMobileMenu} className={mobileLinkClass}>Analytics</Link>
                    <Link to="/instructor/student-results" onClick={closeMobileMenu} className={mobileLinkClass}>Student Results</Link>
                    <Link to="/reports" onClick={closeMobileMenu} className={mobileLinkClass}>Reports</Link>
                  </>
                )}

                <Link to="/payment" onClick={closeMobileMenu} className={mobileLinkClass}>Buy Coins</Link>

                {user?.coins_balance !== undefined && (
                  <div className="sm:hidden flex items-center space-x-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mx-3 my-2">
                    <span className="text-yellow-800 font-semibold">{user.coins_balance}</span>
                    <span className="text-yellow-600 text-sm">coins</span>
                  </div>
                )}

                <Link to="/profile" onClick={closeMobileMenu} className={mobileLinkClass}>
                  Profile: {user?.name}
                </Link>

                <div className="px-3 pt-1">
                  <button
                    onClick={async () => {
                      closeMobileMenu();
                      await handleLogout();
                    }}
                    className="w-full btn btn-secondary text-sm"
                    disabled={logoutLoading}
                  >
                    {logoutLoading ? 'Logging out...' : 'Logout'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

export default Layout;
